import { useEffect, useMemo, useState } from "react";

import { getDiscoveryReadiness } from "../api/discoveryReadinessClient";
import { fetchProjectRequirements } from "../api/projectRequirementsClient";
import { fetchProjectWorkItems } from "../api/projectWorkItemsClient";
import { fetchProjectMeetings } from "../api/projectMeetingsClient";
import { fetchProjectBrief } from "../api/projectBriefClient";
import { fetchProjectRequirementsCoverage } from "../api/projectRequirementsCoverageClient";
import {
  fetchCockpitRuns,
  type CockpitAuditCheck,
  type CockpitRunFilters,
  type CockpitRunSummary,
} from "../api/cockpitRunsClient";
import {
  fetchLongwallsProductization,
  type LongwallsLearningRecommendation,
  type LongwallsProductizationSnapshot,
} from "../api/longwallsProductizationClient";
import type { DiscoveryReadinessContract } from "../types/discoveryReadiness";
import type { ProjectBriefRecord } from "../types/projectBrief";
import type { ProjectRequirementsCoverageReport, RequirementTemplateCoverageEntry } from "../types/requirementsCoverage";
import { DISABLE_COCKPIT_FIXTURES } from "../config/featureFlags";
import {
  IMPACT_DRILLDOWN_REFERENCE,
  buildImpactKpisFromRuns,
  validateImpactKpis,
  type ImpactDrillDown,
  type ImpactKpi,
} from "../domain/impactCockpit";

import { InlineAlert } from "./InlineAlert";
import { NextBestActionPanel } from "./NextBestActionPanel";
import { ReadinessRulesCards } from "./ReadinessRulesCards";
import { SignalsPanel } from "./SignalsPanel";
import { HudCard } from "./HudCard";

interface ProjectCockpitTabProps {
  projectId: string;
}

function formatScore(score?: number): string {
  if (typeof score !== "number") return "—";
  const normalized = score <= 1 ? score * 100 : score;
  return `${Math.round(normalized)}%`;
}

function coverageBadge(status: RequirementTemplateCoverageEntry["coverageStatus"] | undefined): string {
  if (status === "covered") return "hud-badge--pass";
  if (status === "not_covered") return "hud-badge--warn";
  return "";
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatDuration(ms?: number | null): string {
  if (!ms && ms !== 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

function formatPercent(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

function formatCurrencyUsd(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return `$${value.toFixed(2)}`;
}

function readMetricNumber(metrics: CockpitRunSummary["metrics"] | undefined, key: string): number | null {
  if (!metrics) return null;
  const rawValue = (metrics as unknown as Record<string, unknown>)[key];
  if (typeof rawValue !== "number" || Number.isNaN(rawValue)) return null;
  return rawValue;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}

function formatKpiValue(kpi: ImpactKpi): string {
  if (kpi.value === null || Number.isNaN(kpi.value)) return "—";
  if (kpi.unit === "usd") return `$${kpi.value.toFixed(6)}`;
  if (kpi.unit === "%") return `${Number(kpi.value.toFixed(1))}%`;
  return String(kpi.value);
}

function kpiStatusClass(kpi: ImpactKpi): string {
  if (kpi.value === null || Number.isNaN(kpi.value)) return "hud-badge--warn";
  const { warning, critical, direction } = kpi.threshold;
  if (direction === "min") {
    if (kpi.value < critical) return "hud-badge--danger";
    if (kpi.value < warning) return "hud-badge--warn";
    return "hud-badge--pass";
  }
  if (kpi.value > critical) return "hud-badge--danger";
  if (kpi.value > warning) return "hud-badge--warn";
  return "hud-badge--pass";
}

function drillDownStatusClass(status: ImpactDrillDown["status"]): string {
  if (status === "on_track") return "hud-badge--pass";
  if (status === "watch") return "hud-badge--warn";
  return "hud-badge--danger";
}

function formatFreshness(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const deltaMs = Date.now() - date.getTime();
  if (deltaMs < 0) return "—";
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function normalizeAuditChecks(checks?: number | CockpitAuditCheck[]): CockpitAuditCheck[] {
  if (!checks || Array.isArray(checks) === false) return [];
  return checks;
}

function countAuditChecks(checks?: number | CockpitAuditCheck[]): number | null {
  if (Array.isArray(checks)) return checks.length;
  if (typeof checks === "number") return checks;
  return null;
}

function findAuditCheckStatus(checks: CockpitAuditCheck[], id: string): string | undefined {
  return checks.find((check) => check.id === id)?.status;
}

function formatCheckStatus(status?: string): string {
  if (status === "passed") return "PASS";
  if (status === "failed") return "FAIL";
  return "NO_OP";
}

function formatJudgeHeadline(run: CockpitRunSummary): string {
  const judge = run.summary?.judge;
  if (!judge?.finalAction) return "â€”";
  const riskTier = judge.riskTier ? ` / ${judge.riskTier}` : "";
  return `${judge.finalAction}${riskTier}`;
}

function formatRepairLoopHeadline(run: CockpitRunSummary): string {
  const repairLoop = run.summary?.repairLoop;
  if (!repairLoop?.verdict) return "â€”";
  const iterations =
    typeof repairLoop.iterationsUsed === "number" && typeof repairLoop.maxIterations === "number"
      ? ` ${repairLoop.iterationsUsed}/${repairLoop.maxIterations}`
      : "";
  return `${repairLoop.verdict}${iterations}`;
}

function approvalStatusBadgeClass(status: "not_required" | "pending" | "approved" | "rejected"): string {
  if (status === "approved") return "hud-badge--pass";
  if (status === "pending") return "hud-badge--warn";
  if (status === "rejected") return "hud-badge--danger";
  return "";
}

function approvalEscalationBadgeClass(status: "none" | "watch" | "breached" | "escalated"): string {
  if (status === "none") return "hud-badge--pass";
  if (status === "watch") return "hud-badge--warn";
  return "hud-badge--danger";
}

function formatTimer(ms?: number): string {
  if (ms === null || ms === undefined) return "—";
  const absolute = Math.abs(ms);
  const label = formatDuration(absolute);
  return ms < 0 ? `-${label}` : label;
}

function auditIntegrityBadgeClass(status?: "PASS" | "NEEDS_ATTENTION"): string {
  if (status === "PASS") return "hud-badge--pass";
  if (status === "NEEDS_ATTENTION") return "hud-badge--danger";
  return "hud-badge--warn";
}

function releaseDecisionBadgeClass(decision?: "GO" | "PARTIAL_GO" | "NO_GO"): string {
  if (decision === "GO") return "hud-badge--pass";
  if (decision === "PARTIAL_GO") return "hud-badge--warn";
  if (decision === "NO_GO") return "hud-badge--danger";
  return "hud-badge--warn";
}

function attestationControlBadgeClass(status?: "pass" | "watch" | "fail"): string {
  if (status === "pass") return "hud-badge--pass";
  if (status === "watch") return "hud-badge--warn";
  if (status === "fail") return "hud-badge--danger";
  return "hud-badge--warn";
}

type CockpitRunListItem = CockpitRunSummary & { isFixture?: boolean };

const FIXTURE_RUNS: CockpitRunListItem[] = [
  {
    id: "sample-jarvis-001",
    workItemTitle: "Sample: Requirements ingest",
    status: "succeeded",
    stopReason: "PASS",
    createdAt: "2024-03-08T09:30:00.000Z",
    startedAt: "2024-03-08T09:31:05.000Z",
    finishedAt: "2024-03-08T09:38:11.000Z",
    summary: {
      iterations: 2,
      success: true,
      judge: {
        finalAction: "continue",
        finalReason: "all_quality_gates_passed",
        riskTier: "low",
        approvalRequired: false,
        actionCounts: { continue: 2, stop: 0, escalate: 0 },
      },
      repairLoop: {
        verdict: "converged",
        bounded: true,
        iterationsUsed: 2,
        maxIterations: 3,
        patchesProposed: 1,
        patchesApplied: 1,
        guardrailViolationCount: 0,
        failureSignatures: [],
        approvalRequired: false,
        convergenceScore: 0.92,
      },
    },
    artifacts: {
      consoleLog: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/console.log",
      structuredLog: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/logs.jsonl",
      runSummary: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/run_summary.json",
      metrics: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/metrics.json",
      policyReport: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/policy_report.json",
      approvalRequest: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/approval_request.json",
      approvalDecision: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/approval_decision.json",
      auditLog: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/audit_log.jsonl",
      evidenceContract: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/evidence_contract.v2.json",
      judgeReport: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/judge_report.json",
      repairLoopReport: "/api/projects/sample/cockpit/runs/sample-jarvis-001/artifacts/repair_loop_report.json",
      failureReport: undefined,
      diffSummary: undefined,
      evidencePackManifest: undefined,
    },
    isFixture: true,
  },
  {
    id: "sample-jarvis-002",
    workItemTitle: "Sample: API workflow check",
    status: "failed",
    stopReason: "FAIL",
    createdAt: "2024-03-09T12:15:00.000Z",
    startedAt: "2024-03-09T12:16:10.000Z",
    finishedAt: "2024-03-09T12:19:37.000Z",
    summary: {
      iterations: 2,
      success: false,
      judge: {
        finalAction: "stop",
        finalReason: "repeated_failure_detected",
        riskTier: "medium",
        approvalRequired: false,
        actionCounts: { continue: 0, stop: 2, escalate: 0 },
      },
      repairLoop: {
        verdict: "failed",
        bounded: true,
        iterationsUsed: 2,
        maxIterations: 3,
        patchesProposed: 1,
        patchesApplied: 0,
        guardrailViolationCount: 1,
        failureSignatures: ["command_exit_non_zero:test:pnpm test:1"],
        approvalRequired: false,
        convergenceScore: 0.31,
      },
    },
    artifacts: {
      consoleLog: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/console.log",
      structuredLog: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/logs.jsonl",
      runSummary: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/run_summary.json",
      metrics: undefined,
      policyReport: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/policy_report.json",
      approvalRequest: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/approval_request.json",
      approvalDecision: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/approval_decision.json",
      auditLog: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/audit_log.jsonl",
      evidenceContract: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/evidence_contract.v2.json",
      judgeReport: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/judge_report.json",
      repairLoopReport: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/repair_loop_report.json",
      failureReport: "/api/projects/sample/cockpit/runs/sample-jarvis-002/artifacts/failure_report.json",
      diffSummary: undefined,
      evidencePackManifest: undefined,
    },
    isFixture: true,
  },
  {
    id: "sample-jarvis-003",
    workItemTitle: "Sample: UI regression sweep",
    status: "running",
    stopReason: "NO_OP",
    createdAt: "2024-03-10T08:05:00.000Z",
    startedAt: "2024-03-10T08:07:34.000Z",
    finishedAt: undefined,
    summary: {
      iterations: 1,
      success: false,
      judge: {
        finalAction: "escalate",
        finalReason: "approval_required_without_iteration_decision",
        riskTier: "high",
        approvalRequired: true,
        actionCounts: { continue: 0, stop: 0, escalate: 1 },
      },
      repairLoop: {
        verdict: "needs_approval",
        bounded: true,
        iterationsUsed: 1,
        maxIterations: 3,
        patchesProposed: 0,
        patchesApplied: 0,
        guardrailViolationCount: 0,
        failureSignatures: [],
        approvalRequired: true,
        convergenceScore: 0.54,
      },
    },
    artifacts: {
      consoleLog: undefined,
      structuredLog: undefined,
      runSummary: undefined,
      metrics: undefined,
      policyReport: undefined,
      approvalRequest: undefined,
      approvalDecision: undefined,
      auditLog: undefined,
      evidenceContract: "/api/projects/sample/cockpit/runs/sample-jarvis-003/artifacts/evidence_contract.v2.json",
      judgeReport: "/api/projects/sample/cockpit/runs/sample-jarvis-003/artifacts/judge_report.json",
      repairLoopReport: "/api/projects/sample/cockpit/runs/sample-jarvis-003/artifacts/repair_loop_report.json",
      failureReport: undefined,
      diffSummary: "/api/projects/sample/cockpit/runs/sample-jarvis-003/artifacts/diff_summary.json",
      evidencePackManifest: "/api/projects/sample/cockpit/runs/sample-jarvis-003/artifacts/evidence_pack.json",
    },
    isFixture: true,
  },
];

export function ProjectCockpitTab({ projectId }: ProjectCockpitTabProps) {
  const [readiness, setReadiness] = useState<DiscoveryReadinessContract | null>(null);
  const [coverage, setCoverage] = useState<ProjectRequirementsCoverageReport | null>(null);
  const [requirementsCount, setRequirementsCount] = useState<number | null>(null);
  const [workItemsCount, setWorkItemsCount] = useState<number | null>(null);
  const [meetingsCount, setMeetingsCount] = useState<number | null>(null);
  const [brief, setBrief] = useState<ProjectBriefRecord | null>(null);
  const [cockpitRuns, setCockpitRuns] = useState<CockpitRunSummary[]>([]);
  const [runArtifacts, setRunArtifacts] = useState<{ runRegistry?: string; runList?: string } | null>(null);
  const [productization, setProductization] = useState<LongwallsProductizationSnapshot | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [isRunsLoading, setIsRunsLoading] = useState(false);
  const [productizationError, setProductizationError] = useState<string | null>(null);
  const [isProductizationLoading, setIsProductizationLoading] = useState(false);

  const [runFilters, setRunFilters] = useState<CockpitRunFilters>({ limit: 5 });
  const [runFilterDraft, setRunFilterDraft] = useState({
    state: "",
    stopReason: "",
    approvalStatus: "",
    runId: "",
    from: "",
    to: "",
    limit: "5",
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [readinessData, coverageReport, reqs, workItems, meetings, briefRecord] = await Promise.allSettled([
          getDiscoveryReadiness(projectId),
          fetchProjectRequirementsCoverage(projectId),
          fetchProjectRequirements(projectId),
          fetchProjectWorkItems(projectId),
          fetchProjectMeetings(projectId),
          fetchProjectBrief(projectId),
        ]);

        if (cancelled) return;

        if (readinessData.status === "fulfilled") setReadiness(readinessData.value);
        if (coverageReport.status === "fulfilled") setCoverage(coverageReport.value);
        if (reqs.status === "fulfilled") setRequirementsCount(reqs.value.length);
        if (workItems.status === "fulfilled") setWorkItemsCount(workItems.value.length);
        if (meetings.status === "fulfilled") setMeetingsCount(meetings.value.length);
        if (briefRecord.status === "fulfilled") setBrief(briefRecord.value);

        const failures = [readinessData, coverageReport, reqs, workItems, meetings, briefRecord].filter(
          (result) => result.status === "rejected",
        ) as PromiseRejectedResult[];
        if (failures.length) {
          setError(failures.map((failure) => failure.reason?.message ?? String(failure.reason)).join("\n"));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    const loadRuns = async () => {
      setIsRunsLoading(true);
      setRunsError(null);
      try {
        const response = await fetchCockpitRuns(projectId, runFilters);
        if (!cancelled) {
          setCockpitRuns(response.items ?? []);
          setRunArtifacts(response.artifacts ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setRunsError(message);
          setRunArtifacts(null);
        }
      } finally {
        if (!cancelled) setIsRunsLoading(false);
      }
    };
    void loadRuns();
    return () => {
      cancelled = true;
    };
  }, [projectId, runFilters]);

  useEffect(() => {
    let cancelled = false;
    const loadProductization = async () => {
      setIsProductizationLoading(true);
      setProductizationError(null);
      try {
        const payload = await fetchLongwallsProductization(projectId);
        if (!cancelled) {
          setProductization(payload);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setProductizationError(message);
          setProductization(null);
        }
      } finally {
        if (!cancelled) setIsProductizationLoading(false);
      }
    };
    void loadProductization();
    return () => {
      cancelled = true;
    };
  }, [projectId, cockpitRuns.length]);

  const readinessRules = readiness?.explain?.rules ?? [];
  const scorePercent = formatScore(readiness?.score);
  const summary = readiness?.summary ?? { answeredCount: 0, openCount: 0, criticalOpenCount: 0 };

  const pipelineBlocks = useMemo(
    () => [
      {
        title: "Discovery inputs",
        status: readiness?.inputs?.preDiscoveryStatus ?? "pending",
        metrics: [
          { label: "Questions", value: readiness?.inputs?.answeredQuestionsCount ?? "—" },
          { label: "Meetings", value: meetingsCount ?? "—" },
          { label: "Context", value: readiness?.inputs?.customerContextStatus ?? "—" },
        ],
      },
      {
        title: "Requirements",
        status: requirementsCount ?? "—",
        metrics: [
          { label: "Templates answered", value: summary.answeredCount ?? "—" },
          { label: "Open", value: summary.openCount ?? "—" },
          { label: "Critical", value: summary.criticalOpenCount ?? "—" },
        ],
      },
      {
        title: "Execution",
        status: workItemsCount ?? "—",
        metrics: [
          { label: "Work items", value: workItemsCount ?? "—" },
          { label: "Signals", value: readiness?.signals?.length ?? "—" },
          { label: "Decision", value: readiness?.decision?.toUpperCase?.() ?? "—" },
        ],
      },
      {
        title: "Outputs",
        status: brief?.status ?? "pending",
        metrics: [
          { label: "Brief", value: brief?.status ?? "—" },
          { label: "Defaults", value: readiness?.defaultsVersion ?? "—" },
          { label: "Generated", value: readiness?.generatedAt ? new Date(readiness.generatedAt).toLocaleDateString() : "—" },
        ],
      },
    ],
    [brief?.status, meetingsCount, readiness, requirementsCount, summary.answeredCount, summary.criticalOpenCount, summary.openCount, workItemsCount],
  );

  const coverageEntries = useMemo(() => coverage?.entries ?? [], [coverage]);

  const readinessDecisionBadge = readiness?.decision === "pass" ? "hud-badge--pass" : readiness?.decision ? "hud-badge--warn" : "";
  const latestMetricsRun = useMemo(() => cockpitRuns.find((run) => run.metrics), [cockpitRuns]);
  const runMetrics = latestMetricsRun?.metrics;
  const freshnessSource = latestMetricsRun?.finishedAt ?? runMetrics?.generatedAt;
  const runList = useMemo<CockpitRunListItem[]>(() => {
    if (cockpitRuns.length >= 3) return cockpitRuns;
    if (DISABLE_COCKPIT_FIXTURES) return cockpitRuns;
    const merged = [...cockpitRuns, ...FIXTURE_RUNS];
    return merged.slice(0, 3);
  }, [cockpitRuns]);
  const isUsingFixtures = runList.some((run) => run.isFixture);
  const impactKpis = useMemo(() => buildImpactKpisFromRuns(cockpitRuns), [cockpitRuns]);
  const impactValidationIssues = useMemo(() => validateImpactKpis(impactKpis), [impactKpis]);
  const controlTower = useMemo(() => {
    const nowMs = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const recentRuns = cockpitRuns.filter((run) => {
      const createdAtMs = new Date(run.createdAt).getTime();
      return Number.isFinite(createdAtMs) && nowMs - createdAtMs <= sevenDaysMs;
    });
    const completedRuns = cockpitRuns.filter((run) => run.status === "succeeded" || run.status === "failed");
    const passCount = completedRuns.filter((run) => {
      const normalizedStopReason = (run.stopReason ?? "").toUpperCase();
      return normalizedStopReason === "PASS" || run.status === "succeeded";
    }).length;
    const failCount = Math.max(completedRuns.length - passCount, 0);
    const successRate = completedRuns.length ? passCount / completedRuns.length : null;
    const throughputPerDay = recentRuns.length / 7;
    const pendingApprovals = cockpitRuns.filter((run) => run.approval?.status === "pending").length;
    const highRiskOpen = cockpitRuns.filter((run) => {
      const riskTier = run.approval?.riskTier;
      if (riskTier !== "high" && riskTier !== "critical") return false;
      return run.approval?.status !== "approved";
    }).length;
    const integrityAlerts = cockpitRuns.filter((run) => run.auditIntegrity?.status === "NEEDS_ATTENTION").length;
    const runCosts = cockpitRuns
      .map((run) => readMetricNumber(run.metrics, "costUsd"))
      .filter((value): value is number => typeof value === "number");
    const totalCostUsd = runCosts.reduce((acc, value) => acc + value, 0);
    const avgCostPerRun = runCosts.length ? totalCostUsd / runCosts.length : null;
    const leadTimesMs = completedRuns
      .map((run) => {
        const createdAtMs = new Date(run.createdAt).getTime();
        const finishedAtMs = new Date(run.finishedAt ?? "").getTime();
        if (!Number.isFinite(createdAtMs) || !Number.isFinite(finishedAtMs)) return null;
        if (finishedAtMs < createdAtMs) return null;
        return finishedAtMs - createdAtMs;
      })
      .filter((value): value is number => typeof value === "number");
    const averageLeadTimeMs = leadTimesMs.length
      ? leadTimesMs.reduce((acc, value) => acc + value, 0) / leadTimesMs.length
      : null;
    const medianLeadTimeMs = median(leadTimesMs);

    return {
      recentRunsCount: recentRuns.length,
      throughputPerDay,
      passCount,
      failCount,
      successRate,
      pendingApprovals,
      highRiskOpen,
      integrityAlerts,
      runCostSamples: runCosts.length,
      totalCostUsd,
      avgCostPerRun,
      leadTimeSamples: leadTimesMs.length,
      averageLeadTimeMs,
      medianLeadTimeMs,
    };
  }, [cockpitRuns]);
  const releaseRecommendation = productization?.releaseRecommendation;
  const releaseAttestation = productization?.releaseAttestation;
  const marketplacePacks = productization?.marketplace?.packs ?? [];
  const topMarketplacePacks = useMemo(
    () => [...marketplacePacks].sort((left, right) => right.adoptionRuns - left.adoptionRuns).slice(0, 4),
    [marketplacePacks],
  );
  const learningRecommendations = useMemo<LongwallsLearningRecommendation[]>(() => {
    if (!productization?.learningLoop) return [];
    return [
      ...productization.learningLoop.policyAdjustments,
      ...productization.learningLoop.promptAdjustments,
      ...productization.learningLoop.templateAdjustments,
    ]
      .sort((left, right) => right.occurrences - left.occurrences)
      .slice(0, 6);
  }, [productization]);

  return (
    <div className="cockpit-hud">
      {error ? <InlineAlert tone="warning" title="Partial data" description={error} /> : null}
      {impactValidationIssues.length ? (
        <InlineAlert
          tone="warning"
          title="Alerte cohérence KPI"
          description={impactValidationIssues.map((issue) => issue.message).join(" ")}
        />
      ) : null}

      <div className="cockpit-grid">
        <div className="cockpit-left">
          <HudCard
            title="Longwalls Control Tower"
            subtitle="Throughput, quality, risk, cost, and lead time from current cockpit runs"
            bodyClassName="hud-card__body--stack"
          >
            <div className="grid gap-3 md:grid-cols-5">
              <div className="rounded border border-slate-800/60 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Throughput</div>
                <div className="mt-1 text-xl font-semibold text-slate-100">{controlTower.recentRunsCount}</div>
                <div className="text-[11px] text-slate-400">{controlTower.throughputPerDay.toFixed(2)} runs/day (7d)</div>
              </div>
              <div className="rounded border border-slate-800/60 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Quality</div>
                <div className="mt-1 text-xl font-semibold text-slate-100">{formatPercent(controlTower.successRate)}</div>
                <div className="text-[11px] text-slate-400">
                  pass {controlTower.passCount} • fail {controlTower.failCount}
                </div>
              </div>
              <div className="rounded border border-slate-800/60 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Risk</div>
                <div className="mt-1 text-xl font-semibold text-slate-100">{controlTower.highRiskOpen}</div>
                <div className="text-[11px] text-slate-400">
                  pending approvals {controlTower.pendingApprovals} • integrity alerts {controlTower.integrityAlerts}
                </div>
              </div>
              <div className="rounded border border-slate-800/60 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Cost</div>
                <div className="mt-1 text-xl font-semibold text-slate-100">{formatCurrencyUsd(controlTower.avgCostPerRun)}</div>
                <div className="text-[11px] text-slate-400">
                  total {formatCurrencyUsd(controlTower.totalCostUsd)} • samples {controlTower.runCostSamples}
                </div>
              </div>
              <div className="rounded border border-slate-800/60 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Lead time</div>
                <div className="mt-1 text-xl font-semibold text-slate-100">{formatDuration(controlTower.medianLeadTimeMs)}</div>
                <div className="text-[11px] text-slate-400">
                  avg {formatDuration(controlTower.averageLeadTimeMs)} • samples {controlTower.leadTimeSamples}
                </div>
              </div>
            </div>
          </HudCard>

          <HudCard
            title="Impact cockpit — Synthèse COMEX"
            subtitle="KPI consolidés sécurité, fiabilité, coût IA et observabilité"
            bodyClassName="hud-card__body--stack"
          >
            <div className="grid gap-3 md:grid-cols-2">
              {impactKpis.map((kpi) => (
                <div key={kpi.key} className="rounded border border-slate-800/50 bg-slate-900/40 p-3" data-testid={`impact-kpi-${kpi.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="hud-label">{kpi.label}</div>
                    <span className={`hud-badge ${kpiStatusClass(kpi)}`}>{kpi.key}</span>
                  </div>
                  <div className="mt-1 text-xl font-semibold text-slate-100">{formatKpiValue(kpi)}</div>
                  <div className="text-[11px] text-slate-400">Tendance: {kpi.trendDelta === null ? "—" : `${kpi.trendDelta > 0 ? "+" : ""}${kpi.trendDelta}%`}</div>
                  <div className="mt-2 text-[10px] text-slate-400">
                    Source: <span className="font-mono">{kpi.source}</span>
                  </div>
                  <div className="text-[10px] text-slate-500">Fraîcheur: {formatDateTime(kpi.sourceGeneratedAt)} • Refresh: {kpi.refreshFrequency}</div>
                  <div className="text-[10px] text-slate-500">Règle: {kpi.calculationRule}</div>
                </div>
              ))}
            </div>
          </HudCard>

          <HudCard title="Drill-down chantiers / lots" subtitle="Statut actionnable avec tendance et source de preuve">
            <div className="space-y-2" data-testid="impact-drilldown-list">
              {IMPACT_DRILLDOWN_REFERENCE.map((item) => (
                <div key={item.id} className="rounded border border-slate-800/40 bg-slate-950/40 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-100">{item.chantier}</div>
                    <span className={`hud-badge ${drillDownStatusClass(item.status)}`}>{item.status}</span>
                  </div>
                  <div className="mt-1 grid gap-1 text-[11px] text-slate-300 md:grid-cols-2">
                    <span>Lot: {item.lot}</span>
                    <span>Owner: {item.owner}</span>
                    <span>Tendance: {item.trend}</span>
                    <span>KPI: {item.kpiKey}</span>
                    <span className="md:col-span-2">Signal: {item.valueLabel}</span>
                    <span className="md:col-span-2">
                      Source: <span className="font-mono">{item.source}</span> • {formatDateTime(item.sourceGeneratedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </HudCard>

          <HudCard
            title="Delivery pipeline"
            subtitle="70/30 cockpit with a Jarvis-style HUD"
            bodyClassName="hud-card__body--stack"
            footer={isLoading ? <div className="hud-subtle">Updating metrics…</div> : null}
          >
            <div className="hud-metrics">
              {pipelineBlocks.map((block) => (
                <div key={block.title} className="hud-metric">
                  <div className="hud-label">{block.title}</div>
                  <div className="hud-value">{block.status}</div>
                  <div className="hud-metric__grid">
                    {block.metrics.map((metric) => (
                      <div key={metric.label} className="hud-metric__item">
                        <span className="hud-subtle">{metric.label}</span>
                        <span className="hud-value--muted">{metric.value ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </HudCard>

          <HudCard
            title="Readiness summary"
            subtitle="Score, decision, and readiness signals"
            actions={
              readinessDecisionBadge ? <span className={`hud-badge ${readinessDecisionBadge}`}>{readiness?.decision ?? "—"}</span> : null
            }
          >
            <div className="hud-summary">
              <div className="hud-summary__score">
                <div className="hud-label">Score</div>
                <div className="hud-score">{scorePercent}</div>
                <div className="hud-subtle">Status: {readiness?.status ?? "—"}</div>
              </div>
              <div className="hud-summary__counts">
                <div className="hud-chip">Answered {summary.answeredCount}</div>
                <div className="hud-chip hud-chip--warning">Open {summary.openCount}</div>
                <div className="hud-chip hud-chip--danger">Critical {summary.criticalOpenCount}</div>
              </div>
            </div>

            <div className="hud-card__section">
              <div className="hud-label">Rules</div>
              <ReadinessRulesCards projectId={projectId} rules={readinessRules} />
            </div>

            <div className="hud-card__section">
              <SignalsPanel signals={readiness?.signals ?? []} />
            </div>
          </HudCard>

          <HudCard
            title="Coverage snapshot"
            subtitle="Templates and linked requirements"
            actions={<a className="hud-link" href={`/projects/${encodeURIComponent(projectId)}?tab=coverage`}>Open coverage</a>}
          >
            <div className="hud-table">
              <div className="hud-table__header">
                <span>Template</span>
                <span>Title</span>
                <span>Status</span>
                <span>Linked</span>
              </div>
              {(coverageEntries.slice(0, 8) as RequirementTemplateCoverageEntry[]).map((entry) => (
                <div key={entry.templateId ?? entry.templateCode} className="hud-table__row">
                  <span className="hud-value--muted">{entry.templateCode ?? entry.templateId}</span>
                  <span className="hud-subtle">{entry.title}</span>
                  <span className={`hud-badge ${coverageBadge(entry.coverageStatus)}`}>
                    {entry.coverageStatus ?? "—"}
                  </span>
                  <span className="hud-value">{entry.linkedRequirementIds?.length ?? 0}</span>
                </div>
              ))}
              {!coverageEntries.length ? <div className="hud-empty">No coverage entries available.</div> : null}
            </div>
          </HudCard>
        </div>

        <div className="cockpit-right">
          <NextBestActionPanel projectId={projectId} readiness={readiness} />

          <HudCard
            title="Release recommendation engine"
            subtitle="Automated GO/NO_GO signal with confidence and rationale"
            actions={
              releaseRecommendation ? (
                <span className={`hud-badge ${releaseDecisionBadgeClass(releaseRecommendation.decision)}`}>
                  {releaseRecommendation.decision}
                </span>
              ) : null
            }
          >
            {isProductizationLoading && !productization ? <div className="hud-subtle">Computing recommendationâ€¦</div> : null}
            {productizationError ? <div className="hud-subtle text-rose-300">{productizationError}</div> : null}
            {releaseRecommendation ? (
              <div className="hud-card__body--stack">
                <div className="hud-mini-grid">
                  <div>
                    <div className="hud-label">Score</div>
                    <div className="hud-value--muted">{releaseRecommendation.score}/100</div>
                  </div>
                  <div>
                    <div className="hud-label">Confidence</div>
                    <div className="hud-value--muted">{formatPercent(releaseRecommendation.confidence)}</div>
                  </div>
                  <div>
                    <div className="hud-label">Completed runs</div>
                    <div className="hud-value--muted">{releaseRecommendation.kpiSnapshot.completedRuns}</div>
                  </div>
                  <div>
                    <div className="hud-label">Success rate</div>
                    <div className="hud-value--muted">{formatPercent(releaseRecommendation.kpiSnapshot.successRate)}</div>
                  </div>
                </div>
                <div className="space-y-1 text-[11px] text-slate-300">
                  {releaseRecommendation.reasons.map((reason, index) => (
                    <div key={`release-reason-${index}`}>- {reason}</div>
                  ))}
                </div>
                {releaseRecommendation.nextActions.length ? (
                  <div className="space-y-1 text-[11px] text-sky-300">
                    {releaseRecommendation.nextActions.map((action, index) => (
                      <div key={`release-action-${index}`}>Action: {action}</div>
                    ))}
                  </div>
                ) : null}
                {releaseAttestation ? (
                  <div className="rounded border border-emerald-500/20 bg-slate-950/60 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="hud-label">Release attestation</div>
                        <div className="text-[12px] font-semibold text-slate-100">{releaseAttestation.statement}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`hud-badge ${releaseDecisionBadgeClass(releaseAttestation.decision)}`}>
                          {releaseAttestation.decision}
                        </span>
                        <a className="hud-link" href={releaseAttestation.artifactUrl}>
                          release_attestation.json
                        </a>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      <div className="rounded border border-slate-800/60 bg-slate-950/50 p-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-200">Audit chain</span>
                          <span className={`hud-badge ${attestationControlBadgeClass(releaseAttestation.controls.auditChain.status)}`}>
                            {releaseAttestation.controls.auditChain.status}
                          </span>
                        </div>
                        <div className="mt-1 text-slate-400">{releaseAttestation.controls.auditChain.reason}</div>
                      </div>
                      <div className="rounded border border-slate-800/60 bg-slate-950/50 p-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-200">Evidence coverage</span>
                          <span className={`hud-badge ${attestationControlBadgeClass(releaseAttestation.controls.evidenceCoverage.status)}`}>
                            {releaseAttestation.controls.evidenceCoverage.status}
                          </span>
                        </div>
                        <div className="mt-1 text-slate-400">{releaseAttestation.controls.evidenceCoverage.reason}</div>
                      </div>
                      <div className="rounded border border-slate-800/60 bg-slate-950/50 p-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-200">Approvals</span>
                          <span className={`hud-badge ${attestationControlBadgeClass(releaseAttestation.controls.approvals.status)}`}>
                            {releaseAttestation.controls.approvals.status}
                          </span>
                        </div>
                        <div className="mt-1 text-slate-400">{releaseAttestation.controls.approvals.reason}</div>
                      </div>
                      <div className="rounded border border-slate-800/60 bg-slate-950/50 p-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-200">Runtime stability</span>
                          <span className={`hud-badge ${attestationControlBadgeClass(releaseAttestation.controls.runtimeStability.status)}`}>
                            {releaseAttestation.controls.runtimeStability.status}
                          </span>
                        </div>
                        <div className="mt-1 text-slate-400">{releaseAttestation.controls.runtimeStability.reason}</div>
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2 text-[11px] text-slate-300 md:grid-cols-4">
                      <div>
                        <div className="hud-label">Attested at</div>
                        <div className="hud-value--muted">{formatDateTime(releaseAttestation.attestedAt)}</div>
                      </div>
                      <div>
                        <div className="hud-label">Evidence runs</div>
                        <div className="hud-value--muted">{releaseAttestation.evidence.sourceRunIds.length}</div>
                      </div>
                      <div>
                        <div className="hud-label">Artifacts</div>
                        <div className="hud-value--muted">{releaseAttestation.evidence.artifactCount}</div>
                      </div>
                      <div>
                        <div className="hud-label">Integrity alerts</div>
                        <div className="hud-value--muted">{releaseAttestation.summary.integrityAlerts}</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </HudCard>

          <HudCard title="Continuous learning loop" subtitle="Run outcomes mapped to prompt/policy/template improvements">
            {isProductizationLoading && !productization ? <div className="hud-subtle">Building learning signalsâ€¦</div> : null}
            {learningRecommendations.length ? (
              <div className="space-y-2">
                {learningRecommendations.map((item) => (
                  <div key={item.id} className="rounded border border-slate-800/40 bg-slate-950/40 p-2 text-[11px] text-slate-300">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-slate-100">{item.target}</span>
                      <span className="hud-subtle">
                        freq {item.occurrences} • confidence {formatPercent(item.confidence)}
                      </span>
                    </div>
                    <div className="mt-1 text-slate-400">{item.signature}</div>
                    <div className="mt-1 text-slate-200">{item.recommendation}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="hud-subtle">No learning recommendations yet.</div>
            )}
          </HudCard>

          <HudCard title="Template/pattern marketplace" subtitle="Versioned domain packs and live adoption signal">
            {isProductizationLoading && !productization ? <div className="hud-subtle">Loading domain packsâ€¦</div> : null}
            {topMarketplacePacks.length ? (
              <div className="space-y-2">
                {topMarketplacePacks.map((pack) => (
                  <div key={pack.id} className="rounded border border-slate-800/40 bg-slate-950/40 p-2 text-[11px] text-slate-300">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-slate-100">
                        {pack.name} <span className="font-mono text-slate-400">v{pack.version}</span>
                      </span>
                      <span className={`hud-badge ${pack.maturity === "stable" ? "hud-badge--pass" : "hud-badge--warn"}`}>
                        {pack.maturity}
                      </span>
                    </div>
                    <div className="mt-1 text-slate-400">
                      {pack.archetype} • {pack.domain}
                    </div>
                    <div className="mt-1 text-slate-200">{pack.summary}</div>
                    <div className="mt-1 text-slate-400">
                      adoption {pack.adoptionRuns} • success {formatPercent(pack.adoptionSuccessRate)} • last used {formatDateTime(pack.lastUsedAt)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="hud-subtle">No marketplace packs available.</div>
            )}
          </HudCard>

          <HudCard title="Jarvis KPIs" subtitle="Latest run observability metrics">
            <div className="hud-mini-grid">
              <div>
                <div className="hud-label">Latency</div>
                <div className="hud-value--muted">{formatDuration(runMetrics?.latencyMs)}</div>
              </div>
              <div>
                <div className="hud-label">Completeness</div>
                <div className="hud-value--muted">{formatPercent(runMetrics?.completenessRate)}</div>
              </div>
              <div>
                <div className="hud-label">Error rate</div>
                <div className="hud-value--muted">{formatPercent(runMetrics?.errorRate)}</div>
              </div>
              <div>
                <div className="hud-label">E2E success</div>
                <div className="hud-value--muted">{formatPercent(runMetrics?.e2eSuccessRate ?? null)}</div>
              </div>
              <div>
                <div className="hud-label">Freshness</div>
                <div className="hud-value--muted">{formatFreshness(freshnessSource)}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-400">
              {latestMetricsRun?.workItemTitle ?? "Latest run"} • {formatDateTime(latestMetricsRun?.finishedAt)}
            </div>
          </HudCard>

          <HudCard title="Jarvis runs" subtitle="Latest runs for this project">
            <div className="hud-card__body--stack">
              <div className="cockpit-filters">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filters</div>
                <div className="mt-2 grid gap-2 text-[11px] text-slate-200 md:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">Run ID</span>
                    <input
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      placeholder="e.g. jarvis-run-123"
                      value={runFilterDraft.runId}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, runId: event.target.value }))}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">State</span>
                    <select
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      value={runFilterDraft.state}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, state: event.target.value }))}
                    >
                      <option value="">All states</option>
                      <option value="pending">pending</option>
                      <option value="running">running</option>
                      <option value="succeeded">succeeded</option>
                      <option value="failed">failed</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">Stop reason</span>
                    <select
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      value={runFilterDraft.stopReason}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, stopReason: event.target.value }))}
                    >
                      <option value="">All reasons</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                      <option value="NO_OP">NO_OP</option>
                      <option value="NEEDS_APPROVAL">NEEDS_APPROVAL</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">Approval</span>
                    <select
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      value={runFilterDraft.approvalStatus}
                      onChange={(event) =>
                        setRunFilterDraft((prev) => ({ ...prev, approvalStatus: event.target.value }))
                      }
                    >
                      <option value="">All approvals</option>
                      <option value="not_required">not_required</option>
                      <option value="pending">pending</option>
                      <option value="approved">approved</option>
                      <option value="rejected">rejected</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">Limit</span>
                    <input
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      type="number"
                      min={1}
                      value={runFilterDraft.limit}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, limit: event.target.value }))}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">From</span>
                    <input
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      type="datetime-local"
                      value={runFilterDraft.from}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, from: event.target.value }))}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase text-slate-500">To</span>
                    <input
                      className="rounded border border-slate-800/70 bg-slate-900/70 px-2 py-1 text-slate-100"
                      type="datetime-local"
                      value={runFilterDraft.to}
                      onChange={(event) => setRunFilterDraft((prev) => ({ ...prev, to: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <button
                    className="rounded border border-slate-700/80 bg-slate-900/70 px-3 py-1 text-slate-100"
                    onClick={() =>
                      setRunFilters({
                        state: runFilterDraft.state || undefined,
                        stopReason: runFilterDraft.stopReason || undefined,
                        approvalStatus:
                          (runFilterDraft.approvalStatus as "not_required" | "pending" | "approved" | "rejected") ||
                          undefined,
                        runId: runFilterDraft.runId || undefined,
                        from: runFilterDraft.from || undefined,
                        to: runFilterDraft.to || undefined,
                        limit: runFilterDraft.limit ? Number(runFilterDraft.limit) : undefined,
                      })
                    }
                  >
                    Apply filters
                  </button>
                  <button
                    className="rounded border border-slate-800/80 px-3 py-1 text-slate-300"
                    onClick={() => {
                      setRunFilterDraft({
                        state: "",
                        stopReason: "",
                        approvalStatus: "",
                        runId: "",
                        from: "",
                        to: "",
                        limit: "5",
                      });
                      setRunFilters({ limit: 5 });
                    }}
                  >
                    Clear
                  </button>
                  {isRunsLoading ? <span className="hud-subtle">Updating runs…</span> : null}
                </div>
              </div>

              {runsError ? <div className="hud-subtle text-rose-300">{runsError}</div> : null}
              {runArtifacts?.runRegistry || runArtifacts?.runList ? (
                <div className="flex flex-wrap gap-3 text-[11px] text-slate-300">
                  {runArtifacts.runRegistry ? (
                    <a className="cockpit-download-link" href={runArtifacts.runRegistry}>
                      Download run_registry.jsonl
                    </a>
                  ) : null}
                  {runArtifacts.runList ? (
                    <a className="cockpit-download-link" href={runArtifacts.runList}>
                      Download run-list.json
                    </a>
                  ) : null}
                </div>
              ) : null}
              {runList.length === 0 ? (
                <div className="hud-subtle">No runs captured yet.</div>
              ) : (
                runList.map((run) => {
                  const auditChecks = normalizeAuditChecks(run.summary?.audit?.checks);
                  const auditCount = countAuditChecks(run.summary?.audit?.checks);
                  const deliverableStatus = formatCheckStatus(findAuditCheckStatus(auditChecks, "workitem-deliverable"));
                  const dodStatus = formatCheckStatus(findAuditCheckStatus(auditChecks, "workitem-dod"));
                  const approvalStatus = run.approval?.status ?? "not_required";
                  const approvalEscalation = run.approval?.escalation ?? "none";
                  const approvalRoles = run.approval?.requiredRoles?.join(", ") ?? "—";
                  const auditIntegrityStatus = run.auditIntegrity?.status;
                  const auditIntegritySignals = run.auditIntegrity?.totalSignals;
                  const repairLoopScore = run.summary?.repairLoop?.convergenceScore;
                  const deliverableUrl = run.workItemId
                    ? `/api/work-items/${encodeURIComponent(run.workItemId)}/deliverable/download`
                    : null;
                  const artifacts = [
                    { key: "deliverable", label: "deliverable.md", url: deliverableUrl },
                    { key: "consoleLog", label: "console.log", url: run.artifacts?.consoleLog },
                    { key: "structuredLog", label: "logs.jsonl", url: run.artifacts?.structuredLog },
                    { key: "runSummary", label: "run_summary.json", url: run.artifacts?.runSummary },
                    { key: "metrics", label: "metrics.json", url: run.artifacts?.metrics },
                    { key: "policyReport", label: "policy_report.json", url: run.artifacts?.policyReport },
                    { key: "approvalRequest", label: "approval_request.json", url: run.artifacts?.approvalRequest },
                    { key: "approvalDecision", label: "approval_decision.json", url: run.artifacts?.approvalDecision },
                    { key: "auditLog", label: "audit_log.jsonl", url: run.artifacts?.auditLog },
                    { key: "auditIntegrity", label: "audit_integrity.json", url: run.artifacts?.auditIntegrity },
                    { key: "evidenceContract", label: "evidence_contract.v2.json", url: run.artifacts?.evidenceContract },
                    { key: "judgeReport", label: "judge_report.json", url: run.artifacts?.judgeReport },
                    { key: "repairLoopReport", label: "repair_loop_report.json", url: run.artifacts?.repairLoopReport },
                    { key: "failureReport", label: "failure_report.json", url: run.artifacts?.failureReport },
                    { key: "diffSummary", label: "diff_summary.json", url: run.artifacts?.diffSummary },
                    { key: "evidencePackManifest", label: "evidence_pack.json", url: run.artifacts?.evidencePackManifest },
                  ];
                  return (
                    <div key={run.id} className="rounded border border-slate-800/40 bg-slate-900/40 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-100">
                        <div className="font-semibold">
                          {run.workItemTitle ?? `Run ${run.id}`}
                          {run.isFixture ? (
                            <span className="ml-2 rounded bg-slate-800 px-2 py-[1px] text-[10px]">Sample</span>
                          ) : null}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {run.status.toUpperCase()} • {formatDateTime(run.startedAt ?? run.createdAt)}
                        </div>
                        <div className="flex flex-wrap gap-2 text-[10px]">
                          <span className={`hud-badge ${approvalStatusBadgeClass(approvalStatus)}`}>
                            approval {approvalStatus}
                          </span>
                          {approvalStatus === "pending" ? (
                            <span className={`hud-badge ${approvalEscalationBadgeClass(approvalEscalation)}`}>
                              SLA {approvalEscalation}
                            </span>
                          ) : null}
                          {auditIntegrityStatus ? (
                            <span className={`hud-badge ${auditIntegrityBadgeClass(auditIntegrityStatus)}`}>
                              integrity {auditIntegrityStatus.toLowerCase()}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-2 grid gap-2 text-[11px] text-slate-300 md:grid-cols-2">
                        <span>
                          Run ID: <span className="font-mono">{run.id}</span>
                        </span>
                        <span>State: {run.status ?? "—"}</span>
                        <span>Stop reason: {run.stopReason ?? "—"}</span>
                        <span>Stop detail: {run.stopReasonDetail ?? "—"}</span>
                        <span>Spec hash: {run.trace?.runSpecHash ?? "—"}</span>
                        <span>Config hash: {run.trace?.configHash ?? "—"}</span>
                        <span>Approval roles: {approvalRoles}</span>
                        <span>Risk tier: {run.approval?.riskTier ?? "—"}</span>
                        <span>Judge: {formatJudgeHeadline(run)}</span>
                        <span>Repair loop: {formatRepairLoopHeadline(run)}</span>
                        <span>Created: {formatDateTime(run.createdAt)}</span>
                        <span>Started: {formatDateTime(run.startedAt)}</span>
                        <span>Finished: {formatDateTime(run.finishedAt)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-300">
                        <span>Env: {run.environment ?? "—"}</span>
                        <span>Dry-run: {run.dryRun ? "Yes" : "No"}</span>
                        <span>
                          Audit:{" "}
                          {run.summary?.audit?.status
                            ? `${run.summary.audit.status.toUpperCase()}${auditCount !== null ? ` (${auditCount})` : ""}`
                            : "—"}
                        </span>
                        <span>Deliverable: {deliverableStatus}</span>
                        <span>DoD: {dodStatus}</span>
                        <span>Judge reason: {run.summary?.judge?.finalReason ?? "—"}</span>
                        <span>Repair score: {formatPercent(repairLoopScore)}</span>
                        <span>Patches applied: {run.summary?.repairLoop?.patchesApplied ?? "—"}</span>
                        <span>Approval requested: {formatDateTime(run.approval?.requestedAt)}</span>
                        <span>Approval deadline: {formatDateTime(run.approval?.deadlineAt)}</span>
                        <span>SLA remaining: {formatTimer(run.approval?.remainingMs)}</span>
                        <span>SLA overdue: {formatTimer(run.approval?.overdueMs)}</span>
                        <span>
                          Audit integrity:{" "}
                          {auditIntegrityStatus
                            ? `${auditIntegrityStatus}${typeof auditIntegritySignals === "number" ? ` (${auditIntegritySignals})` : ""}`
                            : "—"}
                        </span>
                        <span>
                          Integrity reasons:{" "}
                          {run.auditIntegrity?.reasonCodes?.length
                            ? run.auditIntegrity.reasonCodes.join(", ")
                            : "—"}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-[11px] text-slate-300">
                        {artifacts.map((artifact) => (
                          <div key={artifact.key} className="flex flex-wrap items-center gap-2">
                            {artifact.url ? (
                              <>
                                <a className="text-sky-300 underline" href={artifact.url}>
                                  {artifact.label}
                                </a>
                                <span className="text-[10px] text-emerald-300">Available</span>
                                <span className="break-all text-[10px] text-slate-500">{artifact.url}</span>
                              </>
                            ) : (
                              <>
                                <span className="text-slate-400">{artifact.label}</span>
                                <span className="text-[10px] text-rose-300">Missing</span>
                              </>
                            )}
                          </div>
                        ))}
                        {run.registryArtifact ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <a className="text-sky-300 underline" href={run.registryArtifact}>
                              run_registry.jsonl
                            </a>
                            <span className="text-[10px] text-emerald-300">Available</span>
                            <span className="break-all text-[10px] text-slate-500">{run.registryArtifact}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
              {isUsingFixtures ? (
                <div className="text-[11px] text-slate-500">
                  Showing sample runs to ensure quick lookup while data is loading.
                </div>
              ) : null}
            </div>
          </HudCard>

          <HudCard title="Progress HUD" className="hud-card--glow">
            <div className="hud-card__body--stack">
              <div className="hud-progress">
                <div className="hud-progress__bar">
                  <span
                    style={{
                      width: `${
                        readiness?.explain?.rules?.length
                          ? Math.round(
                              (readiness.explain.rules.filter((rule) => rule.passed).length / readiness.explain.rules.length) *
                                100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <div className="hud-progress__meta">
                  <span>
                    Passed {readiness?.explain?.rules?.filter((rule) => rule.passed).length ?? 0} / {readiness?.explain?.rules?.length ?? "—"}
                  </span>
                  <span>{readiness?.generatedAt ? new Date(readiness.generatedAt).toLocaleString() : "—"}</span>
                </div>
              </div>

              <div className="hud-mini-grid">
                <div>
                  <div className="hud-label">Defaults</div>
                  <div className="hud-value--muted">{readiness?.defaultsVersion ?? "—"}</div>
                </div>
                <div>
                  <div className="hud-label">Decision</div>
                  <div className="hud-value">{readiness?.decision ?? "—"}</div>
                </div>
                <div>
                  <div className="hud-label">Signals</div>
                  <div className="hud-value--muted">{readiness?.signals?.length ?? 0}</div>
                </div>
              </div>
            </div>
          </HudCard>
        </div>
      </div>
    </div>
  );
}
